// Tabs der Portale je Rolle und Objekttyp (design_handoff_portale §4.2/§4.3, Texte aus den Prototypen).
// Entscheidung 22.10.2026: Bereiche ohne Daten werden ausgeblendet statt mit Platzhaltern gezeigt –
// deshalb steuern Schalter wie `haus`/`versammlung`, ob ein Tab erscheint.

export type MieterTab = 'uebersicht' | 'meldungen' | 'termine' | 'dokumente' | 'mietkonto' | 'haus';
export type EigentuemerTab = 'uebersicht' | 'einheiten' | 'einsaetze' | 'finanzen' | 'rechnungen' | 'versammlung' | 'dokumente';

export interface TabInfo<K extends string> { key: K; label: string; tip: string }

export function mieterTabs(opt: { haus: boolean }): TabInfo<MieterTab>[] {
  const alle: (TabInfo<MieterTab> | false)[] = [
    { key: 'uebersicht', label: 'Übersicht', tip: 'Übersicht Ihrer Wohnung' },
    { key: 'meldungen', label: 'Meldungen', tip: 'Gemeldete Schäden und ihr Stand' },
    { key: 'termine', label: 'Termine', tip: 'Anstehende Termine in Wohnung und Haus' },
    { key: 'dokumente', label: 'Dokumente', tip: 'Mietvertrag, Abrechnungen und Protokolle' },
    { key: 'mietkonto', label: 'Mietkonto', tip: 'Miete, Zahlungen und Kaution' },
    opt.haus && { key: 'haus', label: 'Haus', tip: 'Müllabfuhr, Notfallnummern und Hausordnung' },
  ];
  return alle.filter((t): t is TabInfo<MieterTab> => !!t);
}

/** WEG (auch fremdverwaltete WEG mit SEV) gegenüber Miet-/Gebäudeverwaltung (objekte.verwaltungsart). */
export function istWeg(verwaltungsart: string | null | undefined): boolean {
  const v = (verwaltungsart ?? '').trim().toUpperCase();
  return v === 'WEG' || v === 'SEV';
}

export function eigentuemerTabs(opt: { weg: boolean; versammlung: boolean }): TabInfo<EigentuemerTab>[] {
  const alle: (TabInfo<EigentuemerTab> | false)[] = [
    { key: 'uebersicht', label: 'Übersicht', tip: 'Überblick über das gewählte Objekt' },
    { key: 'einheiten', label: 'Einheiten', tip: 'Einheiten, Mieter und Vermietungsstand' },
    // #564 Einsatz-Tracking
    { key: 'einsaetze', label: 'Vorgänge', tip: 'Schäden, Hausmeistereinsätze und Ihre Anfragen mit Stand, Fotos und Kosten' },
    {
      key: 'finanzen', label: 'Finanzen',
      tip: opt.weg ? 'Hausgeld, Rücklage, Jahresabrechnung und Wirtschaftsplan' : 'Mieteinnahmen, Eigentümerabrechnung und Auszahlungen',
    },
    { key: 'rechnungen', label: 'Rechnungen', tip: 'Rechnungen prüfen und freigeben' },
    // Versammlung gibt es nur bei WEG – und nur, wenn es dazu Daten gibt
    opt.weg && opt.versammlung && { key: 'versammlung', label: 'Versammlung', tip: 'Eigentümerversammlung und Beschlusssammlung' },
    { key: 'dokumente', label: 'Dokumente', tip: 'Unterlagen zum Objekt' },
  ];
  return alle.filter((t): t is TabInfo<EigentuemerTab> => !!t);
}

/** Tab aus der URL – unbekannt oder ausgeblendet = erster Tab. */
export function gueltigerTab<K extends string>(tabs: TabInfo<K>[], wunsch: string | undefined): K {
  return tabs.find((t) => t.key === wunsch)?.key ?? tabs[0].key;
}
